import cn from 'classnames';
import React, { PropsWithChildren, ReactElement, useState } from 'react';
import { ElementOrArrayOfElement } from '../common/Types';

interface TabsProps {
  orientation?: TabsOrientation;
  children: ElementOrArrayOfElement<TabProps>;
}

export const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
  (props, ref) => {
    const { children, orientation = 'horizontal' } = props;
    let initialActive = 0;
    if (Array.isArray(children)) {
      const i = (children as Array<
        ReactElement<TabProps> | undefined
      >).findIndex(
        (child: ReactElement<TabProps> | undefined) =>
          !!child && child.props.active === true
      );
      initialActive = i > -1 ? i : 0;
    }
    const [tab, setTab] = useState(initialActive);
    const classNames = cn('clr-tabs', {
      'tabs-vertical': orientation === 'vertical'
    });

    const renderTabList = () => {
      return React.Children.map(children, (child, index) => {
        if (React.isValidElement<TabProps>(child) && child.type === Tab) {
          return React.Children.map(child.props.children, grandChild => {
            if (
              React.isValidElement<TitleProps>(grandChild) &&
              grandChild.type === TabTitle
            ) {
              return React.cloneElement(grandChild, {
                active: tab === index,
                index,
                onClick: onTabClick
              });
            } else {
              return null;
            }
          });
        } else {
          return null;
        }
      });
    };

    const renderTab = () => {
      return React.Children.map(children, (child, index) => {
        if (React.isValidElement<TabProps>(child) && child.type === Tab) {
          return index === tab ? child : null;
        } else {
          return null;
        }
      });
    };

    const onTabClick = (index: number) => {
      setTab(index);
    };

    return (
      <div ref={ref} className={classNames}>
        <ul className="nav" role="tablist">
          {renderTabList()}
        </ul>
        <section>{renderTab()}</section>
      </div>
    );
  }
);

interface TabProps {
  active?: boolean;
  children?: React.ReactNode;
}

export const Tab: React.FC<TabProps> = props => {
  const { children } = props;

  const renderTabContent = () => {
    return React.Children.map(children, child => {
      return React.isValidElement(child) && child.type === TabContent
        ? child
        : null;
    });
  };

  return <>{renderTabContent()}</>;
};

export const TabContent: React.FunctionComponent<React.PropsWithChildren<TabsProps>> = (props) => { return <>{props.children}</> };

type TitleProps = PropsWithChildren<{
  index?: number;
  active?: boolean;
  onClick?: (index: number) => void;
}>;

type TabsOrientation = 'horizontal' | 'vertical';

export const TabTitle = React.forwardRef<HTMLLIElement, TitleProps>(
  (props, ref) => {
    const { children, active, onClick, index } = props;
    const classNames = cn('nav-item');

    const buttonClass = cn('btn', 'btn-link', 'nav-link', {
      active
    });

    const handleClick = () => {
      onClick!(index!);
    };

    return (
      <li ref={ref} className={classNames} onClick={handleClick}>
        <button className={buttonClass} type="button" role="tab">
          {children}
        </button>
      </li>
    );
  }
);
